import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Hourglass, ChevronRight, Radar, MapPin, EyeOff } from 'lucide-react';
import { SEVERITY_STIJL, useSignaalWeergave } from '../WerkplekSignaalLijst';
import type { WerkplekSignal } from '../../services/professionalWerkplekApi';
import { useOpmaak } from './opmaak';
import { useBlokkeerOpdrachtgever } from './blokkeren';

/** Knoptekst per signaal (inkoopvenster); de overige signalen openen met een chevron. */
function actieSleutel(s: WerkplekSignal): string | null {
  if (s.key === 'pool_verzoek') return s.params.tegenvoorstel ? 'pool_tegenvoorstel' : 'pool_verzoek';
  if (s.key === 'pool_contract' || s.key === 'job_radar') return s.key;
  return null;
}

/** 72-uurszandloper van een pooluitnodiging; tikt per minuut. */
const Zandloper: React.FC<{ deadline: string }> = ({ deadline }) => {
  const { t } = useTranslation('professionalDashboard');
  const opmaak = useOpmaak();
  const [nu, setNu] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNu(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const rest = new Date(deadline).getTime() - nu;
  const krap = rest < 24 * 3_600_000;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${krap ? 'text-red-700' : 'text-navy-600'}`}>
      <Hourglass className="w-3.5 h-3.5" />
      {rest > 0 ? t('inkoop.zandloper', { tijd: opmaak.resterend(rest) }) : t('inkoop.verlopen')}
    </span>
  );
};

/** Actiekaart voor de vensters Boekingen, Inkoop en Administratie (teksten uit de werkplek-signalen). */
export const ActieKaart: React.FC<{ signaal: WerkplekSignal }> = ({ signaal: s }) => {
  const { t } = useTranslation('professionalDashboard');
  const { tekst, open } = useSignaalWeergave();
  const { titel, tekst: omschrijving } = tekst(s);
  const stijl = SEVERITY_STIJL[s.severity];
  const actie = actieSleutel(s);
  const radar = s.key === 'job_radar';
  const verberg = useBlokkeerOpdrachtgever();

  return (
    <article
      className={`rounded-lg border border-gray-200 border-l-4 ${radar ? 'border-l-navy-700 bg-navy-50/40' : `${stijl.rand} bg-white`} px-3 py-2.5`}
    >
      <button type="button" onClick={() => open(s)} className="w-full text-left flex items-start gap-2 group">
        {radar ? <Radar className="w-4 h-4 mt-0.5 text-navy-700 flex-shrink-0" /> : <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${stijl.stip}`} />}
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-navy-900 group-hover:underline">{titel}</span>
          {omschrijving && <span className="block text-xs text-navy-500 mt-0.5 line-clamp-2">{omschrijving}</span>}
          {radar && (s.params.plaats || s.params.afstand != null) && (
            <span className="mt-1 flex items-center gap-1 text-xs text-navy-500">
              <MapPin className="w-3 h-3" />
              {[s.params.plaats, s.params.afstand != null ? t('inkoop.afstand', { km: s.params.afstand }) : null].filter(Boolean).join(' · ')}
            </span>
          )}
        </span>
        {!actie && <ChevronRight className="w-4 h-4 text-navy-300 flex-shrink-0 mt-0.5" />}
      </button>
      {(actie || (s.key === 'pool_verzoek' && s.params.deadline)) && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          {s.key === 'pool_verzoek' && s.params.deadline ? <Zandloper deadline={String(s.params.deadline)} /> : <span />}
          {radar && s.params.employer_id ? (
            <button
              type="button"
              onClick={() => verberg.blokkeer(String(s.params.employer_id), s.params.organisatie ? String(s.params.organisatie) : null)}
              disabled={verberg.bezig}
              title={t('blacklist.verbergen')}
              aria-label={t('blacklist.verbergen')}
              className="ml-auto p-1 text-navy-300 hover:text-navy-700"
            >
              <EyeOff className="w-4 h-4" />
            </button>
          ) : null}
          {actie && (
            <button
              type="button"
              onClick={() => open(s)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                s.severity === 'urgent' ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-navy-900 text-white hover:bg-navy-800'
              }`}
            >
              {t(`inkoop.actie.${actie}`)}
            </button>
          )}
        </div>
      )}
    </article>
  );
};
